import {
  OAuthProvider,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithPopup,
  signInWithCredential,
  browserPopupRedirectResolver,
  fetchSignInMethodsForEmail,
} from 'firebase/auth';
import Logger from './Logger';
import FirebaseApp from './FirebaseApp';
import { notifyHandledError } from './ErrorMonitoring';

const { Auth: FirebaseAuth } = FirebaseApp;
const GoogleProvider = new GoogleAuthProvider();
const FacebookProvider = new FacebookAuthProvider();
const AppleProvider = new OAuthProvider('apple.com');

// Firebase restores a persisted session asynchronously after page load, and
// `currentUser` is null for the whole of that window. Reading it directly meant
// any request made before the restore finished — a paywall CTA tapped on
// arrival, most of all — got no token at all. `authStateReady()` resolves once
// the restore has settled either way, so after awaiting it a null `currentUser`
// means genuinely signed out rather than not-yet-known.
async function getSettledCurrentUser() {
  if (!FirebaseAuth) return null;

  try {
    await FirebaseAuth.authStateReady();
  } catch (error) {
    // Never let a failure to settle be the reason a request goes out with no
    // token — fall through to whatever `currentUser` holds right now.
    Logger.warn('auth: authStateReady failed', { error });
  }

  return FirebaseAuth.currentUser;
}

// Every authenticated request in the app is built from this, so a null here is
// a request sent with `Bearer null` (PROD-5419).
//
// The retry covers the second failure mode: `getIdToken()` refreshes an expired
// token over the network, so it can come back empty on a dropped connection
// while the user is perfectly well signed in. One forced retry is worth it
// before giving up; more would hold the CTA longer than a buyer will wait.
async function getUserAuthToken() {
  const user = await getSettledCurrentUser();
  if (!user) return null;

  let token = await user.getIdToken();
  if (!token) {
    token = await user.getIdToken(true);
  }
  if (!token) {
    notifyHandledError(null, {
      message: 'Failed to get auth token for logged in user',
      authUserId: user.uid,
    });
  }
  return token;
}

function getCurrentUser() {
  return FirebaseAuth.currentUser;
}

async function authSignOut() {
  Logger.info('auth: signout');
  try {
    await signOut(FirebaseAuth);
  } catch (error) {
    Logger.error(error);
  }
}

const Auth = {
  instance: FirebaseAuth,
  AppleProvider,
  GoogleProvider,
  FacebookProvider,
  getCurrentUser,
  getSettledCurrentUser,
  getUserAuthToken,
  signOut: authSignOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithPopup,
  signInWithCredential,
  browserPopupRedirectResolver,
  fetchSignInMethodsForEmail,
};

export default Auth;
